import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Save, Loader2, ChevronDown, ExternalLink, Plus, X, Upload, Image } from 'lucide-react';

const BRANDS = [
  { key: 'fliteboard', label: 'Fliteboard', color: '#00D4FF' },
  { key: 'jetwave', label: 'Jetwave', color: '#FF6B00' },
  { key: 'aerofoils', label: 'Aerofoils', color: '#00E01A' },
];

const COMP_LABELS = {
  planche: 'Planche', moteur: 'Moteur', batterie: 'Batterie',
  chargeur: 'Chargeur', aile: 'Aile',
};

const COMP_FIELDS = {
  planche: [
    { key: 'volume_l', label: 'Volume (L)', type: 'number' },
    { key: 'size', label: 'Taille', type: 'text' },
    { key: 'board_type', label: 'Type', type: 'select', options: ['rigide', 'gonflable'] },
    { key: 'max_weight_kg', label: 'Poids max (kg)', type: 'number' },
  ],
  moteur: [],
  batterie: [
    { key: 'capacity', label: 'Capacite', type: 'text' },
    { key: 'weight_kg', label: 'Poids (kg)', type: 'number' },
    { key: 'range_min', label: 'Autonomie (min)', type: 'number' },
  ],
  aile: [{ key: 'size', label: 'Surface', type: 'text' }],
  chargeur: [],
};

function AddProductModal({ brand, compType, onClose, onAdd }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const fields = COMP_FIELDS[compType] || [];
  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      await onAdd({ ...form, brand: brand.key, component_type: compType });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };
  const ic = "w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none";
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#111] border border-[#333] rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[#222] flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold">Nouveau {COMP_LABELS[compType]}</h3>
            <p className="text-xs" style={{ color: brand.color }}>{brand.label}</p>
          </div>
          <button onClick={onClose} className="text-[#8C8F94] hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <input className={ic} placeholder="Nom du produit *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus data-testid="product-name" />
          <textarea className={ic + " h-14 resize-none"} placeholder="Description..." value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          {fields.map(f => (
            <div key={f.key}>
              <label className="text-[#555] text-xs mb-1 block">{f.label}</label>
              {f.type === 'select' ? (
                <select className={ic} value={form[f.key] || ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}>
                  <option value="">--</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input className={ic} type={f.type} value={form[f.key] || ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: f.type === 'number' ? (e.target.value ? Number(e.target.value) : null) : e.target.value }))} />
              )}
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-[#222] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-[#8C8F94] text-sm">Annuler</button>
          <button onClick={handleSubmit} disabled={saving} className="flex items-center gap-2 bg-[#00E01A] text-black font-semibold px-4 py-2 rounded-lg text-sm disabled:opacity-50" data-testid="product-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

function BrandColumn({ brand, usage, matrix, meta, componentTypes, componentsByBrand, onChange, onPriceChange, onPhotoUpload, onPhotoDelete, onAddProduct }) {
  const brandProducts = componentsByBrand?.[brand.key] || {};
  const metaKey = `${usage.value}_${brand.key}`;
  const m = meta?.[metaKey] || {};

  return (
    <div className="flex-1 min-w-[260px] bg-[#0a0a0a] rounded-xl border border-[#222] overflow-hidden">
      {/* Brand header */}
      <div className="p-3 border-b border-[#222] text-center" style={{ backgroundColor: brand.color + '08' }}>
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: brand.color }}>{brand.label}</p>
      </div>

      {/* Photo */}
      <div className="p-3 border-b border-[#1a1a1a]">
        {m.photo ? (
          <div className="relative group">
            <img src={`${process.env.REACT_APP_BACKEND_URL || ''}${m.photo}`} alt={brand.label}
              className="w-full h-32 object-contain rounded-lg bg-[#111]" />
            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
              <label className="p-2 rounded-full bg-white/20 cursor-pointer hover:bg-white/30">
                <Upload size={14} className="text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && onPhotoUpload(usage.value, brand.key, e.target.files[0])} />
              </label>
              <button onClick={() => onPhotoDelete(usage.value, brand.key)} className="p-2 rounded-full bg-red-500/30 hover:bg-red-500/50" data-testid={`photo-delete-${usage.value}-${brand.key}`}>
                <X size={14} className="text-white" />
              </button>
            </div>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-[#333] rounded-lg cursor-pointer hover:border-[#555] transition-colors" data-testid={`photo-upload-${usage.value}-${brand.key}`}>
            <Image size={20} className="text-[#555] mb-1" />
            <span className="text-[#555] text-xs">Ajouter photo</span>
            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && onPhotoUpload(usage.value, brand.key, e.target.files[0])} />
          </label>
        )}
      </div>

      {/* Components */}
      <div className="p-3 space-y-2.5">
        {componentTypes.map(ct => {
          const products = brandProducts[ct] || [];
          const currentKey = matrix[usage.value]?.[brand.key]?.[ct] || '';
          return (
            <div key={ct}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[#555] text-[10px] uppercase tracking-wider">{COMP_LABELS[ct]}</label>
                <button onClick={() => onAddProduct(brand, ct)} className="text-[#555] hover:text-white" title="Ajouter"><Plus size={10} /></button>
              </div>
              <div className="relative">
                <select value={currentKey} onChange={e => onChange(usage.value, brand.key, ct, e.target.value)}
                  className="w-full bg-[#111] border border-[#222] rounded-lg px-3 py-1.5 text-white text-xs appearance-none cursor-pointer hover:border-[#444] focus:outline-none pr-8"
                  data-testid={`sel-${ct}-${usage.value}-${brand.key}`}>
                  <option value="">--</option>
                  {products.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
                </select>
                <ChevronDown size={10} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#555] pointer-events-none" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Price */}
      <div className="p-3 border-t border-[#1a1a1a]">
        <label className="text-[#555] text-[10px] uppercase tracking-wider mb-1 block">Prix a partir de</label>
        <div className="relative">
          <input type="number" value={m.price || ''} placeholder="0"
            onChange={e => onPriceChange(usage.value, brand.key, e.target.value ? Number(e.target.value) : null)}
            className="w-full bg-[#111] border border-[#222] rounded-lg px-3 py-1.5 text-white text-sm font-semibold focus:outline-none focus:border-[#00E01A] pr-8"
            data-testid={`price-${usage.value}-${brand.key}`} />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] text-xs">EUR</span>
        </div>
      </div>
    </div>
  );
}

export default function Comparateur() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matrix, setMatrix] = useState({});
  const [meta, setMeta] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [addModal, setAddModal] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const res = await api.get('/configurateur-profil/admin/matrix');
      setData(res.data);
      setMatrix(res.data.matrix);
      setMeta(res.data.meta || {});
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleChange = (usage, brand, compType, productKey) => {
    setMatrix(prev => ({
      ...prev,
      [usage]: { ...prev[usage], [brand]: { ...(prev[usage]?.[brand] || {}), [compType]: productKey } }
    }));
    setHasChanges(true);
  };

  const handlePriceChange = (usage, brand, price) => {
    const key = `${usage}_${brand}`;
    setMeta(prev => ({ ...prev, [key]: { ...prev[key], usage, brand, price } }));
    setHasChanges(true);
  };

  const handlePhotoUpload = async (usage, brand, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/configurateur-profil/admin/config-photo/${usage}/${brand}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const key = `${usage}_${brand}`;
      setMeta(prev => ({ ...prev, [key]: { ...prev[key], photo: res.data.photo } }));
      toast.success('Photo uploadee');
    } catch (e) { toast.error('Erreur upload photo'); }
  };

  const handlePhotoDelete = async (usage, brand) => {
    try {
      await api.delete(`/configurateur-profil/admin/config-photo/${usage}/${brand}`);
      const key = `${usage}_${brand}`;
      setMeta(prev => { const n = { ...prev }; if (n[key]) { delete n[key].photo; } return n; });
      toast.success('Photo supprimee');
    } catch (e) { toast.error('Erreur suppression photo'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/configurateur-profil/admin/matrix', { matrix });
      // Save all prices
      for (const [key, m] of Object.entries(meta)) {
        if (m.usage && m.brand && m.price !== undefined) {
          await api.put('/configurateur-profil/admin/config-meta', { usage: m.usage, brand: m.brand, price: m.price });
        }
      }
      toast.success('Configuration sauvegardee');
      setHasChanges(false);
    } catch (e) { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleAddProduct = async (productData) => {
    await api.post('/configurateur-profil/admin/products', productData);
    toast.success(`${productData.name} ajoute`);
    setLoading(true);
    await fetchAll();
  };

  if (loading) return <div className="p-6 flex items-center justify-center py-20"><Loader2 size={32} className="text-[#00E01A] animate-spin" /></div>;
  if (!data) return null;

  const usages = data.usages || [];
  const componentTypes = data.component_types || [];

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="comparateur-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Comparateur</h1>
          <p className="text-[#8C8F94] text-sm mt-0.5">Configurez les produits recommandes par utilisation et par marque</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="/guide-efoil" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1D2327] border border-[#333] text-[#8C8F94] hover:text-white hover:border-[#555] transition-all text-sm"
            data-testid="view-public-btn">
            <ExternalLink size={14} /> Vue publique
          </a>
          {hasChanges && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)] disabled:opacity-50"
              data-testid="save-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Sauvegarder
            </button>
          )}
        </div>
      </div>

      {/* Usage sections - each with 2 location sub-sections */}
      {usages.map(usage => {
        const locations = data.locations || [];
        return (
          <div key={usage.value} className="bg-[#111] rounded-2xl border border-[#222] overflow-hidden" data-testid={`section-${usage.value}`}>
            <div className="px-5 py-4 border-b border-[#222]">
              <h2 className="text-white font-bold text-xl tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{usage.label}</h2>
              <p className="text-[#555] text-xs">{(data.levels || []).find(l => l.value === usage.level)?.label || ''}</p>
            </div>
            {locations.map(loc => {
              const compositeUsage = { value: `${usage.value}_${loc.value}`, label: `${usage.label} — ${loc.label}`, level: usage.level };
              return (
                <div key={loc.value}>
                  <div className="px-5 py-2 bg-[#0d0d0d] border-b border-[#1a1a1a]">
                    <p className="text-[#8C8F94] text-xs font-semibold uppercase tracking-wider">{loc.label}</p>
                  </div>
                  <div className="p-4 flex gap-4 overflow-x-auto">
                    {BRANDS.map(brand => (
                      <BrandColumn key={brand.key} brand={brand} usage={compositeUsage}
                        matrix={matrix} meta={meta}
                        componentTypes={componentTypes}
                        componentsByBrand={data.components_by_brand}
                        onChange={handleChange}
                        onPriceChange={handlePriceChange}
                        onPhotoUpload={handlePhotoUpload}
                        onPhotoDelete={handlePhotoDelete}
                        onAddProduct={(b, ct) => setAddModal({ brand: b, compType: ct })} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {addModal && (
        <AddProductModal brand={addModal.brand} compType={addModal.compType}
          onClose={() => setAddModal(null)} onAdd={handleAddProduct} />
      )}
    </div>
  );
}
